/**
 * MyCustomersPanel — Operations Console > My Customers.
 *
 * Iteration 71.2.
 *
 * Filtered, owner-scoped view of the tenant directory. Surfaces the
 * book of business assigned to the signed-in ForgedOps staff member
 * via tenant_profile.internal_owners (account_manager,
 * implementation_manager, customer_success_manager, sales_owner).
 *
 *  GET /api/platform/my-customers/overview
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Briefcase, Loader2, RefreshCw, Users, AlertTriangle,
  TrendingUp, CalendarClock, Archive, CheckCircle2,
  ExternalLink,
} from "lucide-react";

const ROLE_FILTERS = [
  { value: "",                          label: "All Roles" },
  { value: "account_manager",           label: "Account Manager" },
  { value: "implementation_manager",    label: "Implementation Manager" },
  { value: "customer_success_manager",  label: "Customer Success" },
  { value: "sales_owner",               label: "Sales Owner" },
];

const ROLE_LABEL = {
  account_manager:          "AM",
  implementation_manager:   "IM",
  customer_success_manager: "CSM",
  sales_owner:              "Sales",
};

const STAGE_META = {
  active:   { label: "Active",   color: "#86efac" },
  trial:    { label: "Trial",    color: "#fbbf24" },
  archived: { label: "Archived", color: "#94a3b8" },
};

const SEVERITY_COLOR = {
  high:   "#fca5a5",
  medium: "#fbbf24",
  low:    "#7dd3fc",
};


function KpiTile({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="rounded-md border p-3 flex items-start gap-3"
         style={{ borderColor: "var(--fop-border)", background: "var(--fop-surface-1)" }}
         data-testid={testid}>
      <div className="w-8 h-8 rounded inline-flex items-center justify-center shrink-0"
           style={{ background: "color-mix(in srgb, var(--fop-primary) 14%, transparent)" }}>
        <Icon size={15} style={{ color: color || "var(--fop-primary)" }} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          {label}
        </div>
        <div className="text-xl font-semibold text-[var(--fop-text)] tabular-nums">
          {value ?? "—"}
        </div>
      </div>
    </div>
  );
}


function ConversionSparkline({ data }) {
  const max = useMemo(() => {
    let m = 0;
    (data || []).forEach((d) => { if (d.count > m) m = d.count; });
    return m || 1;
  }, [data]);
  const total = (data || []).reduce((s, d) => s + (d.count || 0), 0);

  return (
    <div className="rounded-md border p-3"
         style={{ borderColor: "var(--fop-border)", background: "var(--fop-surface-1)" }}
         data-testid="my-customers-sparkline">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          30-Day Conversions
        </div>
        <div className="text-[10px] font-mono text-[var(--fop-text-2)] tabular-nums">
          Total: {total}
        </div>
      </div>
      <div className="flex items-end gap-[2px] h-12">
        {(data || []).map((d, i) => {
          const pct = max > 0 ? Math.max(2, Math.round((d.count / max) * 100)) : 2;
          return (
            <div key={i}
                 className="flex-1 rounded-sm"
                 title={`${d.date}: ${d.count}`}
                 style={{
                   height: `${pct}%`,
                   background: d.count > 0
                     ? "var(--fop-primary)"
                     : "color-mix(in srgb, var(--fop-text-3) 22%, transparent)",
                 }} />
          );
        })}
      </div>
    </div>
  );
}


function AttentionRow({ item }) {
  return (
    <Link to={`/platform/tenants/${item.tenant_id}`}
          className="flex items-center justify-between gap-3 py-2 px-3 rounded-md hover:bg-[var(--fop-surface-2)] transition-colors group"
          data-testid={`my-customers-attention-${item.tenant_id}`}>
      <div className="min-w-0 flex items-center gap-2.5">
        <div className="w-1.5 h-6 rounded-full shrink-0"
             style={{ background: SEVERITY_COLOR[item.severity] || SEVERITY_COLOR.medium }} />
        <div className="min-w-0">
          <div className="text-sm font-medium text-[var(--fop-text)] truncate">
            {item.display_name || item.slug}
          </div>
          <div className="text-xs text-[var(--fop-text-3)] truncate">
            {item.label}
          </div>
        </div>
      </div>
      <ExternalLink size={13} className="text-[var(--fop-text-3)] group-hover:text-[var(--fop-primary)] shrink-0" />
    </Link>
  );
}


function CustomerRow({ row }) {
  const stage = STAGE_META[row.lifecycle_stage] || { label: row.lifecycle_stage, color: "#cbd5e1" };
  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
    catch { return "—"; }
  };
  return (
    <Link to={`/platform/tenants/${row.tenant_id}`}
          className="grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-[var(--fop-surface-2)] transition-colors"
          data-testid={`my-customers-row-${row.tenant_id}`}>
      <div className="col-span-12 sm:col-span-4 min-w-0">
        <div className="text-sm font-medium text-[var(--fop-text)] truncate">
          {row.display_name || row.slug}
        </div>
        <div className="text-xs text-[var(--fop-text-3)] font-mono truncate">
          {row.slug}{row.industry ? ` · ${row.industry}` : ""}
        </div>
      </div>
      <div className="col-span-6 sm:col-span-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider"
              style={{ color: stage.color }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: stage.color }} />
          {stage.label}
        </span>
      </div>
      <div className="col-span-6 sm:col-span-2 text-xs text-[var(--fop-text-2)] tabular-nums">
        {row.plan_label || row.plan_code || "—"}
        {row.lifecycle_stage === "trial" && row.days_until_expiry != null && (
          <div className="text-[10px] text-[var(--fop-text-3)] font-mono">
            {row.days_until_expiry >= 0
              ? `${row.days_until_expiry}d left`
              : `${-row.days_until_expiry}d expired`}
          </div>
        )}
      </div>
      <div className="col-span-6 sm:col-span-2 text-xs text-[var(--fop-text-2)] tabular-nums">
        <span title="30-day activity">{row.activity_30d || 0} evts</span>
        <div className="text-[10px] text-[var(--fop-text-3)] font-mono">
          {row.counts?.users || 0} users · {row.counts?.projects || 0} proj
        </div>
      </div>
      <div className="col-span-6 sm:col-span-2 flex flex-wrap gap-1 justify-start sm:justify-end">
        {(row.matched_owner_roles || []).map((r) => (
          <span key={r}
                className="inline-flex items-center text-[10px] font-mono px-1.5 py-0.5 rounded border"
                style={{
                  borderColor: "var(--fop-border)",
                  color: "var(--fop-text-2)",
                  background: "color-mix(in srgb, var(--fop-primary) 8%, transparent)",
                }}>
            {ROLE_LABEL[r] || r}
          </span>
        ))}
      </div>
    </Link>
  );
}


export default function MyCustomersPanel() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [roleFilter, setRoleFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/platform/my-customers/overview", {
        params: roleFilter ? { role: roleFilter } : {},
      });
      setOverview(res.data || null);
    } catch (e) {
      // Non-platform-admin will 403 — silently no-op the panel.
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  useEffect(() => { load(); }, [load]);

  // Hide entire panel for non-platform-admins (overview returns 403 → null).
  if (!loading && !overview) return null;

  const tiles = overview?.summary?.tiles || {};
  const sparkline = overview?.summary?.sparkline_30d || [];
  const attention = overview?.summary?.attention || [];
  const customers = overview?.customers || [];

  return (
    <div className="rounded-xl border bg-[var(--fop-surface)]"
         style={{ borderColor: "var(--fop-border)" }}
         data-testid="my-customers-panel">
      <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap"
           style={{ borderColor: "var(--fop-border)" }}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md inline-flex items-center justify-center"
               style={{ background: "color-mix(in srgb, var(--fop-primary) 14%, transparent)" }}>
            <Briefcase size={16} className="text-[var(--fop-primary)]" />
          </div>
          <div>
            <div className="text-[var(--fop-text)] font-semibold">My Customers</div>
            <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider">
              Book of Business · {overview?.actor_email || "—"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} disabled={loading}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs uppercase tracking-wider font-mono"
                  style={{ borderColor: "var(--fop-border)" }}
                  data-testid="my-customers-refresh">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Refresh
          </button>
        </div>
      </div>

      {/* Role filter pills */}
      <div className="px-5 py-3 border-b flex items-center gap-2 flex-wrap"
           style={{ borderColor: "var(--fop-border)" }}>
        {ROLE_FILTERS.map((r) => (
          <button key={r.value || "all"}
                  onClick={() => setRoleFilter(r.value)}
                  className="h-8 px-3 rounded-md text-[11px] font-mono uppercase tracking-wider border transition-colors"
                  style={{
                    borderColor: roleFilter === r.value
                      ? "var(--fop-primary)" : "var(--fop-border)",
                    color: roleFilter === r.value
                      ? "var(--fop-primary)" : "var(--fop-text-2)",
                    background: roleFilter === r.value
                      ? "color-mix(in srgb, var(--fop-primary) 12%, transparent)"
                      : "transparent",
                  }}
                  data-testid={`my-customers-role-${r.value || "all"}`}>
            {r.label}
          </button>
        ))}
      </div>

      {/* KPI tiles + sparkline */}
      <div className="px-5 py-4 border-b grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2.5"
           style={{ borderColor: "var(--fop-border)" }}>
        <KpiTile label="Total Customers"   value={tiles.total_customers}
                 icon={Users} testid="my-customers-tile-total" />
        <KpiTile label="Active"            value={tiles.active_customers}
                 icon={CheckCircle2} color="#86efac" testid="my-customers-tile-active" />
        <KpiTile label="Trial"             value={tiles.trial_customers}
                 icon={CalendarClock} color="#fbbf24" testid="my-customers-tile-trial" />
        <KpiTile label="At Risk"           value={tiles.at_risk}
                 icon={AlertTriangle} color="#fca5a5" testid="my-customers-tile-at-risk" />
        <KpiTile label="Expiring ≤7d"      value={tiles.expiring_7d}
                 icon={CalendarClock} color="#fb923c" testid="my-customers-tile-expiring-7d" />
        <KpiTile label="Converted 30d"     value={tiles.converted_30d}
                 icon={TrendingUp} color="#86efac" testid="my-customers-tile-converted-30d" />
        <KpiTile label="Pending Invites"   value={tiles.pending_invites}
                 icon={Users} testid="my-customers-tile-pending-invites" />
        <KpiTile label="Archived"          value={tiles.archived_customers}
                 icon={Archive} color="#94a3b8" testid="my-customers-tile-archived" />
      </div>

      <div className="px-5 py-4 border-b grid grid-cols-1 lg:grid-cols-3 gap-3"
           style={{ borderColor: "var(--fop-border)" }}>
        <div className="lg:col-span-2">
          <ConversionSparkline data={sparkline} />
        </div>
        <div className="rounded-md border"
             style={{ borderColor: "var(--fop-border)", background: "var(--fop-surface-1)" }}
             data-testid="my-customers-attention-list">
          <div className="px-3 py-2 border-b text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]"
               style={{ borderColor: "var(--fop-border)" }}>
            Top 5 Attention
          </div>
          <div className="p-1">
            {attention.length === 0 ? (
              <div className="px-3 py-4 text-xs text-[var(--fop-text-3)] text-center">
                No attention items. Book of business looks clean.
              </div>
            ) : attention.map((a) => (
              <AttentionRow key={a.tenant_id} item={a} />
            ))}
          </div>
        </div>
      </div>

      {/* Customer list */}
      <div className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : customers.length === 0 ? (
          <div className="p-8 text-center text-[var(--fop-text-3)] text-sm"
               data-testid="my-customers-empty">
            No customers are currently assigned to <span className="font-mono">{overview?.actor_email}</span>
            {roleFilter && (
              <> as <span className="font-mono">{ROLE_LABEL[roleFilter] || roleFilter}</span></>
            )}. Assign yourself via a customer's Profile tab → Internal owners.
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--fop-border)" }}>
            <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-2 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]"
                 style={{ borderColor: "var(--fop-border)" }}>
              <div className="col-span-4">Customer</div>
              <div className="col-span-2">Stage</div>
              <div className="col-span-2">Plan</div>
              <div className="col-span-2">Activity 30d</div>
              <div className="col-span-2 text-right">My Roles</div>
            </div>
            {customers.map((row) => (
              <CustomerRow key={row.tenant_id} row={row} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
